import React from "react";

import { cn } from "lib/utils";

type SkeletonProps = {
  className?: string;
  animate?: boolean;
  "data-testid"?: string;
};

const BASE_CLASSES =
  "relative overflow-hidden rounded-md bg-slate-200/80 text-transparent";
const ANIMATE_CLASSES = "motion-safe:animate-pulse motion-reduce:animate-none";

export const SkeletonBlock = ({
  className,
  animate = true,
  ...props
}: SkeletonProps) => (
  <div
    aria-hidden="true"
    className={cn(BASE_CLASSES, animate && ANIMATE_CLASSES, className)}
    {...props}
  />
);

export const SkeletonText = ({
  className,
  animate,
  ...props
}: SkeletonProps) => (
  <SkeletonBlock className={cn("h-4 w-32", className)} animate={animate} {...props} />
);

export const SkeletonHeading = ({
  className,
  animate,
  ...props
}: SkeletonProps) => (
  <SkeletonBlock className={cn("h-8 w-48", className)} animate={animate} {...props} />
);

export const SkeletonButton = ({
  className,
  animate,
  ...props
}: SkeletonProps) => (
  <SkeletonBlock
    className={cn("h-10 w-36 rounded-full", className)}
    animate={animate}
    {...props}
  />
);

export const SkeletonAvatar = ({
  className,
  animate,
  ...props
}: SkeletonProps) => (
  <SkeletonBlock
    className={cn("size-10 rounded-full", className)}
    animate={animate}
    {...props}
  />
);

export const SkeletonBadge = ({
  className,
  animate,
  ...props
}: SkeletonProps) => (
  <SkeletonBlock
    className={cn("h-6 w-24 rounded-full", className)}
    animate={animate}
    {...props}
  />
);

export const SkeletonLine = ({
  width = "100%",
  className,
  animate,
  ...props
}: SkeletonProps & { width?: string }) => (
  <SkeletonBlock
    className={cn("h-3", className)}
    style={{ width }}
    animate={animate}
    {...props}
  />
);

export const SkeletonCircle = ({
  size = "2.5rem",
  className,
  animate,
  ...props
}: SkeletonProps & { size?: string }) => (
  <SkeletonBlock
    className={cn("rounded-full", className)}
    style={{ width: size, height: size }}
    animate={animate}
    {...props}
  />
);

export const SkeletonInput = ({
  className,
  animate,
  ...props
}: SkeletonProps) => (
  <SkeletonBlock className={cn("h-10 w-full", className)} animate={animate} {...props} />
);

export const SkeletonDivider = ({ className }: { className?: string }) => (
  <div className={cn("h-px w-full bg-slate-200/70", className)} aria-hidden />
);

export const SkeletonGrid = ({
  children,
  className,
  columns = 3,
  gap = "1rem",
}: React.PropsWithChildren<{
  className?: string;
  columns?: number;
  gap?: string;
}>) => (
  <div
    className={cn("grid", className)}
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap }}
  >
    {children}
  </div>
);
